<script setup lang="ts">
import {
  OperatorModuleViewNode,
  OperatorModuleViewTreeNode,
} from '@/views/organization/operator/OperatorModuleView.vue'
import { computed, ref, watch } from 'vue'
import TreeView from '@/components/tree/TreeView.vue'
import useTree, { TreeItemNode } from '@/composables/useTree'

const props = defineProps<{
  loading: boolean
  abbreviation: string
  nodes: OperatorModuleViewTreeNode[]
}>()

const { treeMapItemIdsTo } = useTree()
const selectedStepId = defineModel<string | undefined>('selectedStepId', { default: undefined })
const activatedNodeId = ref<string>()

const nodeIdsToStepIds = computed(() => treeMapItemIdsTo(props.nodes, (n) => n.item.step.id))
watch(activatedNodeId, (v) => {
  selectedStepId.value = v ? nodeIdsToStepIds.value[v] : undefined
})
watch(
  [selectedStepId, nodeIdsToStepIds],
  () => {
    activatedNodeId.value = Object.keys(nodeIdsToStepIds.value).find(
      (k) => nodeIdsToStepIds.value[k] == selectedStepId.value,
    )
  },
  { immediate: true },
)

type NumberedNode = TreeItemNode<OperatorModuleViewNode> & { numbering: number[] }
const nodeProcess = (
  node: TreeItemNode<OperatorModuleViewNode>,
  prevNumbering: number[],
  currentNumber: number,
): NumberedNode => {
  const currentNumbering = [...prevNumbering, currentNumber + 1]
  const children = node.children.map((child, index) => nodeProcess(child, currentNumbering, index))
  return {
    ...node,
    children,
    numbering: currentNumbering,
  }
}
const numberedNodes = computed(() => props.nodes.map((n, i) => nodeProcess(n, [], i)))
const itemProps = (node: TreeItemNode<OperatorModuleViewNode>) => ({
  disabled: node.children.length > 0,
})

const isRail = ref(false)
</script>

<template>
  <v-navigation-drawer :rail="isRail" location="right" width="700">
    <v-btn
      :icon="isRail ? 'chevron_left' : 'chevron_right'"
      density="comfortable"
      :size="40"
      style="position: absolute; left: -20px; top: 5px"
      @click.stop="isRail = !isRail"
    />
    <tree-view
      v-if="!isRail"
      v-model:activated="activatedNodeId"
      style="margin-top: 1em; margin-left: 1em"
      :nodes="numberedNodes"
      open-all
      :item-props="itemProps"
    >
      <template #prepend="{ item }">
        <span style="font-weight: bold"
          >{{ props.abbreviation }} {{ item.numbering.join('.') }}</span
        >
      </template>
      <template #title="{ item }">
        {{ item.item.step.name }}
      </template>
      <template #append="{ item }">
        <v-chip density="compact">{{ item.item.step.stepType.name }}</v-chip>
      </template>
    </tree-view>
  </v-navigation-drawer>
</template>

<style scoped lang="scss"></style>
